import { IssueModel } from 'shared/domain/issue/issueModel';
import { DomainMessagesTypes } from 'shared/domain/messages/message';
import { IssueInDto } from 'shared/dtos/in/issue';
import { chooseErrorMessage } from 'shared/utils/errorHandling';
import { issueEntityToModel } from 'serviceWorker/repository/issue/issueEntityToModel';
import { issueDtoToEntityToSave } from 'serviceWorker/repository/issue/issueDtoToEntity';
import type { IssueEntity } from 'serviceWorker/repository/issue/entity';
import { markIssueDeletedLocally } from 'serviceWorker/services/issues/changeIssueArchival';
import { getExistingIssueModel } from 'serviceWorker/services/issues/issueReads';
import { getUsersSynchronized } from 'serviceWorker/services/users/getUsers';
import { debugLog } from 'shared/logger/debugLog';
import * as dbIssues from '../db/issues/issues';
import * as dbIssuesService from '../db/issues/issuesService';
import { issueContract } from 'shared/domain/issue/issueContract';
import { makeErrorPublisher, makeEventPublisher, makeLoadedEventPublisher, readCollectionPayload } from 'serviceWorker/broadcasts/publishers';
import { scopeOfProject } from 'serviceWorker/helpers/dataScope';

export const broadcastAllIssues = makeLoadedEventPublisher(
  issueContract,
  DomainMessagesTypes.allIssues,
  () => readCollectionPayload(dbIssuesService, async () => {
    const [entities, users] = await Promise.all([
      dbIssues.getAll(),
      getUsersSynchronized(),
    ]);
    return entities.map((entity) =>
      issueEntityToModel(entity, users)
    );
  })
);

const broadcastCreatedIssueModel = makeEventPublisher(issueContract, DomainMessagesTypes.createdEntity);

const broadcastUpdatedIssueModel = makeEventPublisher(issueContract, DomainMessagesTypes.updatedEntity);

const broadcastDeletedIssueId = makeEventPublisher(issueContract, DomainMessagesTypes.deletedEntity);

/** The issue the server saved as a model, without IndexedDB (no site code until the next sync). */
async function issueFromServerAnswer(issueInDto: IssueInDto): Promise<IssueModel> {
  const entity = issueDtoToEntityToSave(issueInDto, '') as unknown as IssueEntity;
  return issueEntityToModel(entity, await getUsersSynchronized());
}

/**
 * Stores the issue the server saved and reads it back as a model. An answer
 * of a project no longer selected is not stored, but still answered: the
 * server saved the change and the page may wait for it.
 */
async function storeSavedIssue(issueInDto: IssueInDto, projectId?: string): Promise<IssueModel> {
  const isStored = await scopeOfProject(projectId).write(async () => {
    await dbIssues.addBatch([issueInDto]);
    return true;
  });
  return isStored ? getExistingIssueModel(issueInDto._id) : issueFromServerAnswer(issueInDto);
}

/** Tells the pages an issue changed in IndexedDB outside the HTTP queue (e.g. its linked documentation). */
export async function broadcastSavedIssue(issueId: string): Promise<void> {
  broadcastUpdatedIssueModel(await getExistingIssueModel(issueId));
}

/** Saves the created issue and answers its create (`uniqueId`) with the model. */
export const broadcastCreatedIssue = async (
  issueInDto: IssueInDto,
  uniqueId?: string,
  projectId?: string
): Promise<void> => {
  try {
    broadcastCreatedIssueModel(await storeSavedIssue(issueInDto, projectId), uniqueId);
  } catch (error) {
    broadcastCreatedIssueError(error, chooseErrorMessage(error), uniqueId);
  }
};

/** Saves the changed issue and answers its edit (`uniqueId`) with the model. */
export const broadcastUpdatedIssue = async (
  issueInDto: IssueInDto,
  uniqueId?: string,
  projectId?: string
): Promise<void> => {
  try {
    broadcastUpdatedIssueModel(await storeSavedIssue(issueInDto, projectId), uniqueId);
  } catch (error) {
    broadcastUpdatedIssueError(error, chooseErrorMessage(error), uniqueId);
  }
};

/** The server archived the issue: keeps it archived and answers the change (`uniqueId`). */
export const broadcastDeletedIssue = async (
  issueId: string,
  uniqueId?: string,
  projectId?: string
): Promise<void> => {
  try {
    // Marked locally only in its project; answered in any case.
    await scopeOfProject(projectId).write(() => markIssueDeletedLocally(issueId));
    broadcastDeletedIssueId({ _id: issueId }, uniqueId);
  } catch (error) {
    broadcastDeletedIssueError(error, chooseErrorMessage(error), uniqueId);
  }
};

const broadcastIssueError = makeErrorPublisher(
  issueContract
);

/** A failed change is its outcome event with `error`, `messageId` and the change's `uniqueId`. */
function makeIssueChangeErrorBroadcaster(
  type: DomainMessagesTypes.createdEntity | DomainMessagesTypes.updatedEntity | DomainMessagesTypes.deletedEntity
) {
  return function issueChangeErrorBroadcaster(
    error: unknown,
    messageId?: string | null,
    uniqueId?: string
  ): void {
    debugLog('broadcastIssueChangeError', type, error, messageId, uniqueId);
    broadcastIssueError(type, error, { messageId: messageId ?? null, uniqueId });
  };
}

export const broadcastCreatedIssueError = makeIssueChangeErrorBroadcaster(
  DomainMessagesTypes.createdEntity
);

export const broadcastUpdatedIssueError = makeIssueChangeErrorBroadcaster(
  DomainMessagesTypes.updatedEntity
);

export const broadcastDeletedIssueError = makeIssueChangeErrorBroadcaster(
  DomainMessagesTypes.deletedEntity
);
